<template>
  <Modal :open="open" size="big" @close="$emit('close')">
    <div>
      <h1 class="text-2xl font-bold">{{ t("title") }}</h1>
      <p v-if="analyzedAt" class="mt-1 text-xs text-gray-500">
        {{ t("analyzed_at", { date: formattedAnalyzedAt }) }}
      </p>
    </div>

    <div v-if="errorMessage" class="mt-6 rounded-md border-l-4 border-red-400 bg-red-50 p-4 text-sm text-red-700">
      <p class="font-semibold">{{ t("unavailable_title") }}</p>
      <p class="mt-1">{{ errorMessage }}</p>
    </div>

    <div
      v-else-if="!analyzedAt"
      class="mt-6 rounded-md border-l-4 border-gray-300 bg-gray-50 p-4 text-sm text-gray-700"
    >
      {{ t("not_analyzed") }}
    </div>

    <div v-else class="mt-6 space-y-6">
      <div class="flex items-center gap-3">
        <span class="text-sm font-semibold text-gray-700">{{ t("overall_verdict") }}</span>
        <span :class="verdictBadgeClass" class="inline-flex items-center rounded-full px-3 py-1 text-xs font-bold">
          {{ verdictLabel(t, verdict) }}
        </span>
      </div>

      <div v-if="extractedFields.length > 0">
        <h2 class="text-sm font-semibold text-gray-800">{{ t("extracted_data") }}</h2>
        <dl class="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div v-for="field in extractedFields" :key="field.label" class="flex gap-2 text-sm">
            <dt class="font-medium text-gray-500">{{ field.label }} :</dt>
            <dd class="text-gray-800">{{ field.value || "—" }}</dd>
          </div>
        </dl>
      </div>

      <VerificationTable v-if="checks.length > 0" :title="t('field_checks')" :checks="checks" />

      <div v-if="dateDeltas.length > 0">
        <h2 class="text-sm font-semibold text-gray-800">{{ t("date_coherence") }}</h2>
        <p class="mt-0.5 text-xs text-gray-500">{{ t("date_coherence_hint") }}</p>
        <dl class="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div v-for="delta in dateDeltas" :key="delta.key" class="flex gap-2 text-sm">
            <dt class="font-medium text-gray-500">{{ delta.label }} :</dt>
            <dd class="text-gray-800">{{ delta.value }}</dd>
          </div>
        </dl>
      </div>

      <AiAnalysisFeedbackForm
        :analysable-type="analysableType"
        :analysable-id="analysableId"
        analysis-type="anti_theft_device_invoice"
      />
    </div>

    <div v-if="claimActive && (analyzedAt || errorMessage)" class="mt-4 border-t border-gray-100 pt-4">
      <button
        type="button"
        :disabled="retrying"
        class="inline-flex items-center rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 focus:outline-hidden disabled:opacity-50"
        @click="retryAnalysis"
      >
        {{ t("retry_button") }}
      </button>
    </div>
  </Modal>
</template>

<script>
import { useI18n } from "vue-i18n";
import { Modal } from "@estaly/ui";
import { useRetryAnalysis } from "@/composables/useRetryAnalysis";
import {
  verdictLabel,
  badgeClassFor,
  mapExtractedFields,
  mapChecks,
  mapDateDeltas,
  formatAnalyzedAt,
} from "@/components/claim/details/antiTheftDeviceInvoiceAnalysisFormatter";
import VerificationTable from "@/components/claim/details/VerificationTable.vue";
import AiAnalysisFeedbackForm from "@/components/claim/details/AiAnalysisFeedbackForm.vue";

export default {
  name: "AntiTheftDeviceInvoiceAnalysisModal",
  components: { Modal, VerificationTable, AiAnalysisFeedbackForm },
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const { retrying, retryAnalysis } = useRetryAnalysis("anti_theft_device_invoice");
    return { t, locale, retrying, retryAnalysis: () => retryAnalysis(props.analysableId, emit) };
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    analysis: {
      type: Object,
      default: null,
    },
    analyzedAt: {
      type: [String, Date],
      default: null,
    },
    analysableId: {
      type: Number,
      required: true,
    },
    analysableType: {
      type: String,
      required: true,
    },
    claimActive: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "retry-success"],
  computed: {
    errorMessage() {
      return this.analysis?.error || null;
    },
    verdict() {
      return this.analysis?.overallVerdict || null;
    },
    extractedFields() {
      return mapExtractedFields(this.t, this.analysis);
    },
    checks() {
      return mapChecks(this.t, this.analysis);
    },
    dateDeltas() {
      return mapDateDeltas(this.t, this.analysis);
    },
    verdictBadgeClass() {
      return badgeClassFor(this.verdict);
    },
    formattedAnalyzedAt() {
      return formatAnalyzedAt(this.analyzedAt, this.locale);
    },
  },
  methods: {
    verdictLabel,
  },
};
</script>

<i18n src="./AntiTheftDeviceInvoiceAnalysisModal.i18n.json"></i18n>
